import { useTranslations } from 'next-intl'

import type {
  CustomerType as ContractCustomerType,
  ContractValidity,
  PriceType,
} from '@/shared/graphql/schema/commonBackend/graphql'
import type { CustomerType } from '@/shared/store/slices/selectedContractSlice'

type UseContractEnergyPriceExtraTextProps = {
  customerType: ContractCustomerType
  priceType: PriceType
  validity?: ContractValidity | null
}

type EnergyPriceExtraTextKey =
  | `hourlyPrice.${CustomerType}.continuous`
  | `hourlyPrice.${CustomerType}.fixedTerm`
  | 'consumptionImpact'
  | undefined

export const getEnergyPriceExtraText = (
  customerType: CustomerType,
  priceType: PriceType,
  contractValidity?: ContractValidity | null,
): { key: EnergyPriceExtraTextKey; sign?: string } | undefined => {
  switch (priceType) {
    case 'SPOT':
      if (contractValidity?.kind === 'CONTINUOUS') {
        return { key: `hourlyPrice.${customerType}.continuous`, sign: '+' }
      }

      if (contractValidity?.kind === 'FIXED_TERM') {
        return { key: `hourlyPrice.${customerType}.fixedTerm`, sign: '+' }
      }
      break
    case 'HYBRID':
      return { key: 'consumptionImpact', sign: '+/-' }
    default:
      return undefined
  }
}

/**
 * Get energy element extra text and sign if needed.
 */
export const useContractEnergyPriceExtraText = ({
  customerType: contractCustomerType,
  priceType,
  validity,
}: UseContractEnergyPriceExtraTextProps) => {
  const tPurchaseFlow = useTranslations('purchaseFlow')

  const customerType: CustomerType =
    contractCustomerType === 'ENTERPRISE' ? 'enterprise' : 'private'
  const energyPriceExtraText = getEnergyPriceExtraText(customerType, priceType, validity)
  return energyPriceExtraText?.key && tPurchaseFlow.has(energyPriceExtraText.key)
    ? {
        text: tPurchaseFlow(energyPriceExtraText.key),
        sign: energyPriceExtraText?.sign,
      }
    : undefined
}
